import { LineChartOutlined } from "@ant-design/icons";
import { Alert, Col, Empty, Form, InputNumber, Row, Skeleton, Statistic, Typography } from "antd";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { SECTIONS } from "@/shared/config";
import { formatDate, formatMoney, formatPercent } from "@/shared/lib";
import { SectionCard } from "@/shared/ui";

import type { PreviewParams } from "../model/usePreview";
import { useYieldPreview } from "../model/usePreview";

/**
 * Превью настроек генератора (AD-02).
 *
 * Главное здесь — предупреждение о расхождении: если заявленная на карточке
 * доходность отличается от расчётной больше чем в полтора раза, оператор
 * обязан это увидеть до сохранения. Порог именно такой: точного совпадения от
 * демо-генератора требовать бессмысленно, а двукратный разрыв — это уже другой
 * продукт, чем обещает карточка.
 *
 * Сверка идёт с ЧИСТОЙ доходностью, а не с валовой: под графиком в мини-аппе
 * стоит подпись «12M NET», и на роботе с пятнадцатипроцентной ставкой валовая
 * цифра расходилась бы с карточкой всегда — предупреждение горело бы постоянно
 * и читать его перестали бы.
 */
const MISMATCH_RATIO = 1.5;

export function YieldPreviewCard({
  params,
  declaredYield,
  base,
  onBaseChange,
}: {
  params: PreviewParams;
  declaredYield: string;
  /**
   * Сумма, на которой идёт прогон. Отдельным полем, а не полем робота:
   * фиксированной цены у робота больше нет, а минимум — порог входа, а не
   * типичный капитал. По умолчанию страница подставляет рекомендованную сумму.
   */
  base?: string;
  onBaseChange?: (value: string | null) => void;
}) {
  const { t } = useTranslation();
  const { data, isFetching, isError } = useYieldPreview(params);

  const declared = Number(declaredYield);
  const actual = data ? Number(data.expected_yield_12m_net) : null;
  const mismatch =
    actual !== null &&
    declared > 0 &&
    actual > 0 &&
    (declared / actual > MISMATCH_RATIO || actual / declared > MISMATCH_RATIO);

  return (
    <SectionCard
      color={SECTIONS.robots.color}
      icon={<LineChartOutlined aria-hidden />}
      title={t("robots.preview")}
    >
      <Typography.Paragraph type="secondary">{t("robots.previewHint")}</Typography.Paragraph>

      {onBaseChange ? (
        <Form.Item label={t("robots.previewBase")} extra={t("robots.previewBaseHint")}>
          <InputNumber
            stringMode
            min="0"
            step="100"
            style={{ width: "50%" }}
            value={base}
            onChange={(value) => {
              onBaseChange(value);
            }}
          />
        </Form.Item>
      ) : null}

      {isError && <Alert type="warning" showIcon title={t("robots.rangeError")} />}

      {!data && !isError && (isFetching ? <Skeleton active /> : <Empty description={false} />)}

      {data && (
        <>
          {mismatch && actual !== null && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              title={t("robots.mismatch", {
                declared: declaredYield,
                actual: data.expected_yield_12m_net,
              })}
            />
          )}

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Statistic
                title={t("robots.expectedYield")}
                value={formatPercent(data.expected_yield_12m, { sign: "always" })}
                styles={{
                  content: {
                    color:
                      Number(data.expected_yield_12m) < 0
                        ? "var(--ant-color-error)"
                        : "var(--ant-color-success)",
                    fontWeight: 600,
                  },
                }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title={t("robots.expectedYieldNet")}
                value={formatPercent(data.expected_yield_12m_net, { sign: "always" })}
                styles={{
                  content: {
                    color:
                      Number(data.expected_yield_12m_net) < 0
                        ? "var(--ant-color-error)"
                        : "var(--ant-color-success)",
                    fontWeight: 600,
                  },
                }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title={t("robots.maxDrawdown")}
                value={formatPercent(data.max_drawdown)}
                styles={{ content: { color: "var(--ant-color-error)", fontWeight: 600 } }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title={t("robots.maxDrawdownNet")}
                value={formatPercent(data.max_drawdown_net)}
                styles={{ content: { color: "var(--ant-color-error)", fontWeight: 600 } }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={t("robots.feeTotal")}
                value={formatMoney(data.fee_total)}
              />
            </Col>
            <Col span={8}>
              <Statistic title={t("robots.positiveDays")} value={`${String(data.positive_days)} / 365`} />
            </Col>
            <Col span={8}>
              <Statistic
                title={t("robots.bestDay")}
                value={formatPercent(data.best_day, { sign: "always" })}
              />
            </Col>
            <Col span={8}>
              <Statistic title={t("robots.worstDay")} value={formatPercent(data.worst_day)} />
            </Col>
          </Row>

          <div style={{ height: 260, marginTop: 20 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.chart} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                {/* Градиент вместо плоской заливки: у кривой эквити важна
                    форма, и сплошной прямоугольник цвета её глушит. */}
                <defs>
                  <linearGradient id="yield-preview-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--ant-color-primary)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="var(--ant-color-primary)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--ant-color-split)"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  minTickGap={48}
                  tick={{ fontSize: 11, fill: "var(--ant-color-text-secondary)" }}
                  stroke="var(--ant-color-border)"
                />
                <YAxis
                  width={72}
                  tick={{ fontSize: 11, fill: "var(--ant-color-text-secondary)" }}
                  stroke="var(--ant-color-border)"
                />
                <Tooltip
                  labelFormatter={(value) => formatDate(typeof value === "string" ? value : "")}
                  formatter={(value) =>
                    formatMoney(typeof value === "string" ? value : "0.00", { sign: "always" })
                  }
                  contentStyle={{
                    background: "var(--ant-color-bg-elevated)",
                    border: "1px solid var(--ant-color-border)",
                    borderRadius: 10,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="var(--ant-color-primary)"
                  strokeWidth={2}
                  fill="url(#yield-preview-fill)"
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </SectionCard>
  );
}
